import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import BadgeIcon from '@mui/icons-material/Badge';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArticleIcon from '@mui/icons-material/Article';
import GavelIcon from '@mui/icons-material/Gavel';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { getActiveBrandConfig, isBrandActive, ENV_VERIFIED_TRAINER } from 'src/config/environment.config';
import { ElementInterface } from 'src/Objects/interfaces/AppInterface';
import useHome from 'src/Hooks/Dashboard/useHome';
import Teachr from 'src/Objects/models/Teachr';
import Layout from 'src/Components/Layout';
import TeachrRepository from 'src/Repositories/TeachrRepository';
import { userSetInfoSiret } from 'src/Redux/Actions/UserActions';
import { ProfilContextType, ProfilProvider } from 'src/Contexts/ProfilContexte';
import SidebarNavLayout from 'src/Components/Ui/SidebarNavLayout';
import FirstCompletProfil from './CompletProfil/FirstCompletProfil';
import SecondCompletProfil from './CompletProfil/SecondCompletProfil';
import LastCompletProfil from './CompletProfil/LastCompletProfil';
import CooperativeForm from './CompletProfil/Cooperative/CooperativeForm';
import CooperativeDocumentStep from './CompletProfil/Cooperative/CooperativeDocumentStep';
import CooperativeDocumentUploadStep from './CompletProfil/Cooperative/CooperativeDocumentUploadStep';
import CooperativeConfirmationStep from './CompletProfil/Cooperative/CooperativeConfirmationStep';
import SapProcurationForm from './CompletProfil/Procuration&DeclarationSAP/SapProcurationForm';
import SapAeProcurationForm from './CompletProfil/ProcurationAe&Sap/SapAeProcurationForm';
import SapDocumentUploadStep from './CompletProfil/Procuration&DeclarationSAP/SapDocumentUploadStep';
import SapDocumentStep from './CompletProfil/Procuration&DeclarationSAP/SapDocumentStep';
import ThirdCompletProfil from './CompletProfil/ThirdCompletProfil';

interface CompletProfilStepProps {
    teachr: Teachr;
}

export interface StateInterface {
    businessType: string;
    hasSiret: boolean;
    hasAe: boolean;
    hasCooperative: boolean;
}


const CompletProfilStep = ({ teachr }: CompletProfilStepProps) => {
    const location = useLocation();
    const initialStep = typeof (location.state as { step?: number })?.step === 'number'
        ? (location.state as { step: number }).step
        : 0;
    const currentProcuration = parseInt(teachr?.procuration || '0');
    const [step, setStep] = useState(initialStep);
    const dispatch = useDispatch();
    const clientsData = useSelector((state: any) => state?.student?.clients || null);
    const formData = useSelector((state: any) => currentProcuration === 10 ? state.user?.cooperativeFormData : state.user?.sapFormData);
    const [state, setState] = useState<StateInterface>({
        businessType: teachr?.businessType || '',
        hasSiret: false,
        hasAe: false,
        hasCooperative: false
    });
    const isVT = isBrandActive(ENV_VERIFIED_TRAINER);
    const isScoop = !!(teachr?.scoop && currentProcuration === 10);
    const isA = currentProcuration === 10; // Coopérative
    const isB = currentProcuration === 20; // Procuration SAP
    const isC = currentProcuration === 30; // Procuration AE+SAP
    const isD = currentProcuration === 40; // Déclaration SAP
    const isProcSap = isB;
    const isProcAeAndSap = isC;
    const isDeclaration = isD;
    const navigate = useNavigate();

    const nextStep = useCallback((value: number) => {
        setStep(value);
    }, [setStep]);

    const { getOnboardingSteps } = useHome();
    const handleAddUser = useCallback(() => { }, []);
    const handleDeclare = useCallback(() => { }, []);

    const totalStudents: number = clientsData?.totalValidated ?? 0;
    const totalStudentsNoActive: number = clientsData?.totalNotValidated ?? 0;
    const { steps: onboardingSteps } = React.useMemo(() =>
        getOnboardingSteps(handleAddUser, handleDeclare, teachr, totalStudents, totalStudentsNoActive),
        [getOnboardingSteps, handleAddUser, handleDeclare, teachr, totalStudents, totalStudentsNoActive]
    );

    const handleRefreshSiret = useCallback(async () => {
        if (teachr?.RCS) {
            try {
                const response = await TeachrRepository.getInfoBySiret(teachr?.RCS);
                if (response) {
                    dispatch(userSetInfoSiret(response));
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des informations SIRET :', error);
            }
        }
    }, [dispatch, teachr]);

    const contextValue: ProfilContextType = useMemo(() => ({
        nextStep,
        teachr,
        dispatch,
        currentProcuration,
        setState,
        formData,
        handleRefreshSiret
    }), [dispatch, nextStep, teachr, currentProcuration, formData, handleRefreshSiret]);
    
    useEffect(() => {
        switch (step) {
            case 0:
                if (isScoop && !!teachr?.businessType) nextStep(2);
                else if ((isProcSap || isDeclaration) && !!teachr?.businessType) nextStep(1);
                break;
            case 1:
                if ((isProcSap || isDeclaration) && !!teachr?.businessType) nextStep(2);
                break;
            case 2:
                if (isScoop) {
                    if (!formData && !teachr?.proofMandat && !isProcAeAndSap) {
                        nextStep(2)
                    } else {
                        if (teachr?.proofIdentity && teachr?.proofSignSap) nextStep(4);
                    }
                } else {
                    if (isProcAeAndSap) {
                        if (isProcAeAndSap && !teachr?.RCS && !!teachr?.proofIsAE) nextStep(2)
                        else nextStep(5)
                    }
                    else if (isD && teachr?.proofSignSap) {
                        if (!formData && !teachr?.proofMandat && !isProcAeAndSap) nextStep(2)
                        else nextStep(4);
                    }
                    else if (!isD && teachr?.proofIdentity) {
                        if (!formData && !teachr?.proofMandat && !isProcAeAndSap) nextStep(2)
                        else nextStep(4);
                    }
                }
                break;
            case 3:
                if (isScoop) {
                    if (!formData && !teachr?.proofMandat && !isProcAeAndSap) {
                        nextStep(2)
                    } else if (teachr?.proofIdentity && teachr?.proofSignSap) {
                        nextStep(4);
                    }
                } else {
                   if (isProcAeAndSap) {
                        if (isProcAeAndSap && !teachr?.RCS && !!teachr?.proofIsAE) nextStep(2)
                        else nextStep(5)
                    }
                    else if (isD && teachr?.proofSignSap) {
                        if (!formData && !teachr?.proofMandat && !isProcAeAndSap) nextStep(2)
                        else nextStep(4);
                    }
                    else if (!isD && teachr?.proofIdentity) {
                        if (!formData && !teachr?.proofMandat && !isProcAeAndSap) nextStep(2)
                        else nextStep(4);
                    }
                }
                break;
            case 4:
                if (isScoop) {
                    if (!teachr?.proofMandat && !formData) nextStep(2);
                    else if (teachr?.proofMandat) nextStep(6);
                } else {
                   if (isProcAeAndSap) {
                        if (isProcAeAndSap && !teachr?.RCS && !!teachr?.proofIsAE) nextStep(2)
                        else nextStep(5)
                    }
                    else if (!teachr?.proofMandat && !formData && !isProcAeAndSap) nextStep(2);
                    else if (teachr?.proofSignSap) nextStep(5);
                }
                break;
            case 5:
                // if (isProcAeAndSap && !teachr?.RCS && !!teachr?.proofIsAE) nextStep(2)
                if (teachr?.proofMandat) nextStep(6);
                break;
            case 6:
                if (isScoop && teachr?.scoop === '40' && teachr?.proofMandat) {
                    navigate(`/${getActiveBrandConfig()}/tableau-de-bord`);
                }
                break;
            default:
                break;
        }

    }, [step, isScoop, isProcSap, isDeclaration, isD, isVT, teachr, nextStep, navigate, formData, handleRefreshSiret, isProcAeAndSap]);

    const renderScreen = () => {
        switch (step) {
            case 0:
                return <FirstCompletProfil />;
            case 1:
                return <SecondCompletProfil />;
            case 2:
                if (isScoop) return <CooperativeForm onPrevious={() => nextStep(0)} />;
                if (isProcAeAndSap) return <SapAeProcurationForm onNext={() => nextStep(5)} />;
                return <SapProcurationForm onNext={() => nextStep(3)} onPrevious={() => nextStep(2)} />;
            case 3:
                return isScoop
                    ? <CooperativeDocumentUploadStep />
                    : <SapDocumentUploadStep onPrevious={() => nextStep(2)} onNext={() => nextStep(4)} />;
            case 4:
                return isScoop
                    ? <CooperativeDocumentStep />
                    : <SapDocumentStep onPrevious={() => nextStep(3)} onNext={() => nextStep(5)} />;
            case 5:
                return <ThirdCompletProfil nextStep={nextStep} teachr={teachr} />;
            case 6:
                return isScoop ? <CooperativeConfirmationStep /> : <LastCompletProfil />;
            default:
                return <FirstCompletProfil />;
        }
    };

    // ── Onboarding elements (completion status from hook) ──
    const profielElement = onboardingSteps[2].children;
    const element1 = profielElement ? profielElement[0] : null;
    const element2 = profielElement && profielElement[1] ? profielElement[1] : null;
    const element3 = profielElement && profielElement[2] ? profielElement[2] : null;
    const element4 = profielElement && profielElement[3] ? profielElement[3] : null;

    // ── Brand-specific labels ──
    const isNoAE = state.businessType === 'NOAE' || teachr?.businessType === 'NOAE';
    const hasFormSteps = isA || isB || isD; // 2a and 2b sub-items
    // Non-VT cooperative skip the "Conditions des services" step (step 5)
    const hasCGSSubItem = (isA && isVT) || isB;

    const procurationLabel =
        isA ? 'Coopérative' :
            isB ? 'Procuration SAP' :
                isC ? 'Procuration AE+SAP' :
                    isD ? 'Déclaration SAP' :
                        'Adhésion';

    const doc3aLabel =
        isA && !isVT ? 'Règlement intérieur' :
            isA ? "Annexe d'adhésion" :
                isB ? 'Document SAP' :
                    isD ? 'Conditions des services' :
                        'Document';

    // ── Sub-item helpers ──
    const iconSize = { fontSize: '16px' } as const;

    const item1Children: Omit<ElementInterface, 'children'>[] = [
        {
            title: "Type d'entreprise",
            value: 0,
            icons: <CategoryIcon style={iconSize} />,
            completed: state.hasAe || teachr?.businessType ? true : false,
            locked: false,
        },
        ...(!isNoAE ? [{
            title: 'SIRET',
            value: 1,
            icons: <BadgeIcon style={iconSize} />,
            completed: state.hasSiret || teachr?.RCS ? true : false,
            locked: false,
        }] : []),
    ];

    const item2Children: Omit<ElementInterface, 'children'>[] = hasFormSteps ? [
        {
            title: 'Complétude du formulaire',
            value: 2,
            icons: <NoteAltIcon style={iconSize} />,
            completed: step > 2,
            locked: step < 2,
        },
        {
            title: 'Ajout des documents',
            value: 3,
            icons: <UploadFileIcon style={iconSize} />,
            completed: step > 3,
            locked: step < 3,
        },
    ] : [];

    const item3Children: Omit<ElementInterface, 'children'>[] = [
        {
            title: doc3aLabel,
            value: 4,
            icons: <ArticleIcon style={iconSize} />,
            completed: step > 4,
            locked: step < 4,
        },
        ...(hasCGSSubItem ? [{
            title: 'Conditions des services',
            value: 5,
            icons: <GavelIcon style={iconSize} />,
            completed: step > 5,
            locked: step < 5,
        }] : []),
    ];

    // ── Sidebar menu config ──
    const profilCompletConfig: ElementInterface[] = [
        {
            title: element1?.title || 'Statut juridique',
            value: 0,
            icons: <BusinessIcon style={{ fontSize: '24px' }} />,
            completed: element1?.completed,
            locked: element1?.locked,
            disabledAction: true,
            children: item1Children,
        },
        {
            title: procurationLabel,
            value: 2,
            icons: <AssignmentIcon style={{ fontSize: '24px' }} />,
            completed: element2?.completed,
            locked: element2?.locked,
            action: element2?.action,
            disabledAction: true,
            children: item2Children,
        },
        {
            title: element3?.title || 'Conditions des services',
            value: 4,
            icons: <DescriptionIcon style={{ fontSize: '24px' }} />,
            completed: element3?.completed,
            locked: element3?.locked,
            disabledAction: true,
            children: item3Children,
        },
        {
            title: element4?.title || 'Validation de compte',
            value: 6,
            icons: <HowToRegIcon style={{ fontSize: '24px' }} />,
            completed: element4?.completed,
            locked: element4?.locked,
            disabledAction: true,
            action: element4?.action,
            id: element4?.id,
        },
    ];

    return (
        <ProfilProvider value={contextValue}>
            <Layout>
                <SidebarNavLayout
                    data={profilCompletConfig}
                    value={step}
                    onValueChange={nextStep}
                    className="md:w-1/4 w-full h-screen"
                    classNameBody="md:w-3/4 w-full h-screen"
                >
                    {() => renderScreen()}
                </SidebarNavLayout>
            </Layout>
        </ProfilProvider>
    );
};

export default CompletProfilStep;
